import { useState } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "./context/AuthContext.jsx";
import AdminPage from "./pages/Admin.jsx";
import AgricFinancePage from "./pages/AgricFinance.jsx";
import AssetFinancePage from "./pages/AssetFinance.jsx";
import ContributionsPage from "./pages/Contributions.jsx";
import CrowdfundingPage from "./pages/Crowdfunding.jsx";
import DashboardPage from "./pages/Dashboard.jsx";
import ForgotPasswordPage from "./pages/ForgotPassword.jsx";
import KycPage from "./pages/Kyc.jsx";
import LoansPage from "./pages/Loans.jsx";
import LoginPage from "./pages/Login.jsx";
import OnboardingPage from "./pages/Onboarding.jsx";
import RegisterPage from "./pages/Register.jsx";
import SavingsPage from "./pages/Savings.jsx";
import VerifyPage from "./pages/Verify.jsx";
import { defaultRouteForMember, hasOpsAccess, opsRoles } from "./utils/routes.js";

const memberLinks = [
  { to: "/dashboard", label: "Home", icon: "home" },
  { to: "/contributions", label: "Daily contributions", icon: "contribution" },
  { to: "/savings", label: "Target savings", icon: "savings" },
  { to: "/loans", label: "Loans", icon: "loans" },
  { to: "/asset-finance", label: "Asset finance", icon: "asset" },
  { to: "/agric-finance", label: "Agric finance", icon: "agric" },
  { to: "/crowdfunding", label: "Crowdfunding", icon: "community" },
  { to: "/kyc", label: "KYC & documents", icon: "profile" },
  { to: "/onboarding", label: "Member profile", icon: "settings" }
];

const primaryMobileLinks = memberLinks.slice(0, 4);
const moreMobileLinks = memberLinks.slice(4);

function AppIcon({ name, className = "h-5 w-5" }) {
  const paths = {
    home: <><path d="M3 10.8 12 3l9 7.8" /><path d="M5.5 9.5V21h13V9.5M9 21v-7h6v7" /></>,
    contribution: <><path d="M12 3v18M17 7.5c0-1.7-2.2-3-5-3s-5 1.3-5 3 2.2 3 5 3 5 1.3 5 3-2.2 3-5 3-5-1.3-5-3" /></>,
    savings: <><path d="M5 8.5A7 7 0 0 1 18.5 11H21v5h-2.2a7.4 7.4 0 0 1-2.3 2.4V21h-3v-1.5H9V21H6v-2.6A7 7 0 0 1 5 8.5Z" /><path d="M15.5 8h.01M8 4.5c1.5-1.3 4.5-1.5 6.5-.5" /></>,
    loans: <><path d="M4 10h16M5 10v9m4-9v9m6-9v9m4-9v9M3 19h18M12 3l9 4H3l9-4Z" /></>,
    asset: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8 15l2.5-3 2 2 3.5-5 2 3M8 8h.01" /></>,
    agric: <><path d="M12 21V10M12 14c-4 0-7-2.5-7-6 4 0 7 2.5 7 6ZM12 11c0-4 2.5-7 6-7 0 4-2.5 7-6 7Z" /></>,
    community: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></>,
    profile: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></>,
    more: <><circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></>,
    close: <><path d="m6 6 12 12M18 6 6 18" /></>
  };
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name] || paths.home}
    </svg>
  );
}

function ProtectedRoute({ children }) {
  const { accessToken, authReady, member } = useAuth();
  if (!authReady) {
    return <FullScreenLoading />;
  }
  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }
  if (hasOpsAccess(member)) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { accessToken, authReady, member } = useAuth();
  if (!authReady) {
    return <FullScreenLoading />;
  }
  if (!accessToken) {
    return <Navigate to="/login" replace />;
  }
  const role = member?.role;
  if (!opsRoles.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function HomeRedirect() {
  const { accessToken, authReady, member } = useAuth();
  if (!authReady) {
    return <FullScreenLoading />;
  }
  return <Navigate to={accessToken ? defaultRouteForMember(member) : "/login"} replace />;
}

function FullScreenLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 text-sm text-slate-600">
      Loading...
    </div>
  );
}

function AppShell({ children }) {
  const { member, logout } = useAuth();
  const isOpsUser = hasOpsAccess(member);
  const [moreOpen, setMoreOpen] = useState(false);
  return (
    <div className="min-h-screen bg-sand/40">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-black/5 bg-sand/90 px-4 py-3 backdrop-blur-xl md:hidden">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-accent font-display text-lg text-white">D</span>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Divine Coop</p>
            <p className="text-sm font-semibold text-ink">Hello, {member?.first_name || "Member"}</p>
          </div>
        </div>
        <button type="button" className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-accent shadow-sm" aria-label="Notifications">
          <AppIcon name="bell" />
        </button>
      </header>

      <div className="mx-auto flex min-h-screen max-w-7xl gap-8 px-4 py-4 md:px-6 md:py-6">
        <aside className="card-surface sticky top-6 hidden h-[calc(100vh-3rem)] w-72 shrink-0 flex-col rounded-[28px] p-5 md:flex">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Divine Coop</p>
            <p className="font-display text-3xl text-accent">
              {isOpsUser ? "Operations" : "Finance Suite"}
            </p>
          </div>
          <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1 text-sm">
            {isOpsUser ? (
              <>
                <p className="px-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                  Administrative hub
                </p>
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-3 font-semibold transition ${
                      isActive
                        ? "bg-[rgba(129,178,154,0.24)] text-ink"
                        : "text-slate-600 hover:bg-white/60"
                    }`
                  }
                >
                  Admin Hub
                </NavLink>
              </>
            ) : (
              memberLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-4 py-3 transition ${
                      isActive
                        ? "bg-[rgba(224,122,95,0.18)] text-ink"
                        : "text-slate-600 hover:bg-white/60"
                    }`
                  }
                >
                  <AppIcon name={link.icon} className="h-4 w-4" />
                  {link.label}
                </NavLink>
              ))
            )}
          </nav>
          <div className="mt-5 rounded-2xl bg-white/70 p-4 text-xs text-slate-600">
            <p className="font-semibold text-ink">{member?.first_name}</p>
            <p className="text-[11px]">{member?.email}</p>
            <p className="mt-2 uppercase tracking-widest text-[10px] text-slate-400">
              {member?.role || "member"}
            </p>
            <button
              type="button"
              onClick={logout}
              className="mt-4 w-full rounded-xl bg-accent px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white"
            >
              Sign out
            </button>
          </div>
        </aside>
        <main className="min-w-0 flex-1 pb-24 md:pb-6">{children}</main>
      </div>

      {!isOpsUser && (
        <nav className="fixed inset-x-3 bottom-3 z-40 grid grid-cols-5 rounded-[24px] border border-white/60 bg-white/95 px-2 pb-[max(0.55rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_18px_55px_rgba(16,42,67,0.2)] backdrop-blur-xl md:hidden" aria-label="Primary navigation">
          {primaryMobileLinks.map((link) => (
            <NavLink key={link.to} to={link.to} className={({ isActive }) => `flex flex-col items-center gap-1 rounded-2xl px-1 py-2 text-[10px] font-semibold transition ${isActive ? "bg-accent/10 text-accent" : "text-slate-400"}`}>
              <AppIcon name={link.icon} className="h-5 w-5" />
              <span className="max-w-full truncate">{link.label.split(" ")[0]}</span>
            </NavLink>
          ))}
          <button type="button" onClick={() => setMoreOpen(true)} className="flex flex-col items-center gap-1 rounded-2xl px-1 py-2 text-[10px] font-semibold text-slate-400">
            <AppIcon name="more" className="h-5 w-5" />
            More
          </button>
        </nav>
      )}

      {moreOpen && (
        <div className="fixed inset-0 z-50 flex items-end bg-ink/40 p-3 backdrop-blur-sm md:hidden" role="dialog" aria-modal="true" aria-label="More services">
          <div className="w-full rounded-[30px] bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Member services</p>
                <h2 className="mt-1 text-2xl font-semibold text-accent">More</h2>
              </div>
              <button type="button" onClick={() => setMoreOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600" aria-label="Close menu">
                <AppIcon name="close" />
              </button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {moreMobileLinks.map((link) => (
                <NavLink key={link.to} to={link.to} onClick={() => setMoreOpen(false)} className="flex items-center gap-3 rounded-2xl bg-sand/70 p-4 text-sm font-semibold text-ink">
                  <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white text-accent"><AppIcon name={link.icon} /></span>
                  {link.label}
                </NavLink>
              ))}
            </div>
            <button type="button" onClick={logout} className="mt-4 w-full rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">Sign out</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppShell>
              <DashboardPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <AppShell>
              <OnboardingPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kyc"
        element={
          <ProtectedRoute>
            <AppShell>
              <KycPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contributions"
        element={
          <ProtectedRoute>
            <AppShell>
              <ContributionsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/savings"
        element={
          <ProtectedRoute>
            <AppShell>
              <SavingsPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/loans"
        element={
          <ProtectedRoute>
            <AppShell>
              <LoansPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crowdfunding"
        element={
          <ProtectedRoute>
            <AppShell>
              <CrowdfundingPage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/asset-finance"
        element={
          <ProtectedRoute>
            <AppShell>
              <AssetFinancePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agric-finance"
        element={
          <ProtectedRoute>
            <AppShell>
              <AgricFinancePage />
            </AppShell>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppShell>
              <AdminPage />
            </AppShell>
          </AdminRoute>
        }
      />
    </Routes>
  );
}
